import { useState } from "react";
import moment from "moment";
import { getAvailabityByDay } from "../services/getAvailabilityByDay";
import { useEffect } from "react";

/**
 * Procesa los valores de disponibilidad del objetivo
 * Devuelve los porcentajes de disponibilidad "Real"
 *
 * @typedef {Object} DisponibilidadObjetivo
 * @property {number} uptime
 * @property {number} downtime
 * @property {number} parcial
 *
 * @typedef {Object} Data
 * @property {DisponibilidadObjetivo[]} data - Data disponbilidad por objetivo
 *
 * @typedef {Object} DataProcess
 * @property {number} uptime - % uptime
 * @property {number} downtime - % downtime
 * @property {number} noMonitoreo - % no Monitoreo
 * @property {number} mantencion - % mantención
 *
 * @param {*} param0
 * @returns DataProcess
 * @author David Maldonado
 * @version 1.0.0
 */
const processData = ({ data }) => {
    const totalReal = data.uptime + data.downtime + data.parcial;
    const uptime = data.uptime + data.parcial;
    const downtimeReal =
        totalReal === 0 ? 0.0 : (data.downtime * 100) / totalReal;
    const uptimeReal = totalReal === 0 ? 0.0 : (uptime * 100) / totalReal;

    return {
        uptime: uptimeReal.toFixed(2),
        downtime: downtimeReal.toFixed(2),
        noMonitoreo: data["no monitoreo"].toFixed(2),
        mantencion: (data.evento_cliente + data.marcado_atentus).toFixed(2),
    };
};

/**
 * @function getDataFromApi
 *
 * @typedef {Object} AvailabilityByDay
 * @property {boolean} isLoading
 * @property {getDataFromApi} getDataFromApi - Función de carga de los datos del objetivo desde la api
 * @property {DisponibilidadObjetivo[]} data - Lista disponibilidad por día y objetivo
 *
 * @typedef {Object} StatePeriod
 * @property {Date} - startDate
 * @property {Date} - endDate
 *
 * @typedef {Object} Props
 * @param {string} period - Periodo activo/seleccionado
 * @param {StatePeriod} statePeriod - Rango de fechas activo/seleccionado
 * @param {number} scheduleId - Horario activo/seleccionado
 * @param {boolean} autoFetch - Indicador si se ejecutará la consulta a los datos de forma inmediata
 *
 * @param {Props}
 * @returns AvailabilityByDay
 *
 * @author David Maldonado
 * @version 1.0.0
 */
const useObjectivesAvailabilityByDay = ({
    period,
    statePeriod,
    scheduleId,
    autoFetch,
}) => {
    const [data, setObjectivesData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        async function fetch() {
            if (autoFetch) {
                const response = await getDataFromApi();

                setObjectivesData(response);
            }
        }
        fetch();
    }, [period, scheduleId, statePeriod]);

    const getDataFromApi = async () => {
        try {
            setIsLoading(true);
            const beginDate =
                period === "c"
                    ? moment(statePeriod.startDate).format("YYYY-MM-DD")
                    : null;
            const finalDate =
                period === "c"
                    ? moment(statePeriod.endDate).format("YYYY-MM-DD")
                    : null;

            const objectivesStorage = sessionStorage.getItem(
                "objetivosSpecial"
            );
            const objectives = objectivesStorage
                ? JSON.parse(objectivesStorage)
                : [];
            const objectivesData = await Promise.all(
                objectives.map(async (objective) => {
                    const dataResponse = await getAvailabityByDay({
                        objectiveId: objective.id,
                        period,
                        beginDate,
                        finalDate,
                        scheduleId,
                    });
                    return dataResponse.map(({ fecha, data: dateInfo }) => {
                        const splitDate = fecha.split("-");                                                    
                        return {
                            name: objective.nombre,
                            fecha: `${splitDate[2]}-${splitDate[1]}-${splitDate[0]}`,
                            data: processData({ data: dateInfo }),
                        };
                    });
                })
            );

            let result = [];
            objectivesData.forEach((item) => {
                result = [...result, ...item];
            });
            return result;
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    return { isLoading, getDataFromApi, data };
};

export { useObjectivesAvailabilityByDay };
